import React, { useState, useEffect } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { 
  Calendar as CalendarIcon, 
  Plus, 
  ChevronLeft, 
  ChevronRight, 
  ExternalLink,
  Loader,
  RefreshCw,
  Filter,
  Search,
  MoreHorizontal,
  Trash2,
  Edit,
  Clock,
  MapPin,
  Users,
  X
} from 'lucide-react';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

// Mock data for initial development - will be replaced with CalDAV API
const MOCK_EVENTS = [
  {
    id: '1',
    title: 'Mietertermin: Familie Schmidt',
    start: new Date(new Date().setHours(10, 0, 0, 0)),
    end: new Date(new Date().setHours(11, 30, 0, 0)),
    extendedProps: {
      location: 'Musterstraße 123, 12345 Berlin',
      description: 'Besprechung wegen Heizungsreparatur',
      attendees: ['Max Mustermann', 'Anna Schmidt'],
      category: 'meeting',
      hubUrl: 'https://hub.clara360.de/apps/calendar/edit/personal/1'
    }
  },
  {
    id: '2',
    title: 'Wartung: Heizungsanlage',
    start: new Date(new Date().setDate(new Date().getDate() + 1)).setHours(9, 0, 0, 0),
    end: new Date(new Date().setDate(new Date().getDate() + 1)).setHours(12, 0, 0, 0),
    extendedProps: {
      location: 'Hauptstraße 42, 12345 Berlin',
      description: 'Jährliche Wartung der Heizungsanlage',
      attendees: ['Techniker Müller', 'Hausmeister Schmidt'],
      category: 'maintenance',
      hubUrl: 'https://hub.clara360.de/apps/calendar/edit/personal/2'
    }
  },
  {
    id: '3',
    title: 'Eigentümerversammlung',
    start: new Date(new Date().setDate(new Date().getDate() + 3)).setHours(18, 0, 0, 0),
    end: new Date(new Date().setDate(new Date().getDate() + 3)).setHours(20, 0, 0, 0),
    extendedProps: {
      location: 'Konferenzraum, Hauptstraße 1, 12345 Berlin',
      description: 'Jährliche Eigentümerversammlung mit Budgetbesprechung',
      attendees: ['Alle Eigentümer', 'Verwaltung'],
      category: 'meeting',
      hubUrl: 'https://hub.clara360.de/apps/calendar/edit/personal/3'
    }
  }
];

// Clara design system colors
const CLARA_COLORS = {
  stone: {
    light: '#f5f5f4',
    main: '#78716c',
    dark: '#44403c'
  },
  gold: {
    light: '#fef9c3',
    main: '#eab308',
    dark: '#854d0e'
  },
  tron: {
    light: '#dbeafe',
    main: '#3b82f6',
    dark: '#1e40af'
  }
};

// Event category colors
const CATEGORY_COLORS = {
  meeting: CLARA_COLORS.tron.main,
  maintenance: CLARA_COLORS.gold.main,
  deadline: '#ef4444', // red
  reminder: '#8b5cf6', // purple
  other: CLARA_COLORS.stone.main
};

const ClaraCalendar = () => {
  const [events, setEvents] = useState(MOCK_EVENTS);
  const [loading, setLoading] = useState(false);
  const [currentView, setCurrentView] = useState('dayGridMonth');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showEventDetails, setShowEventDetails] = useState(false);
  
  // Function to fetch events from CalDAV server
  // This will be replaced with actual API call
  const fetchEvents = async (start, end) => {
    setLoading(true);
    
    try {
      // Mock API call - will be replaced with actual CalDAV API
      const response = await fetch(`http://localhost:3005/api/cal/events?from=${start}&to=${end}`);
      const data = await response.json();
      setEvents(data);
      setLoading(false);
      
    } catch (error) {
      console.error('Error fetching calendar events:', error);
      setLoading(false);
    }
  };
  
  // Initial load
  useEffect(() => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    
    fetchEvents(start.toISOString(), end.toISOString());
  }, []);
  
  // Handle date range change
  const handleDatesSet = (dateInfo) => {
    fetchEvents(dateInfo.startStr, dateInfo.endStr);
  };
  
  // Handle event click
  const handleEventClick = (info) => {
    setSelectedEvent(info.event);
    setShowEventDetails(true);
  };
  
  // Format date for display
  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };
  
  // Format time for display
  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString('de-DE', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  // Open event in Hub
  const openInHub = (url) => {
    window.open(url, '_blank');
  };
  
  // Render event content
  const renderEventContent = (eventInfo) => {
    const category = eventInfo.event.extendedProps?.category || 'other';
    
    return (
      <div className="flex items-center gap-1 px-1 py-0.5 overflow-hidden">
        <div 
          className="w-2 h-2 rounded-full flex-shrink-0" 
          style={{ backgroundColor: CATEGORY_COLORS[category] }}
        />
        <div className="truncate text-xs font-medium">
          {eventInfo.event.title}
        </div>
      </div>
    );
  };
  
  return (
    <div className="space-y-6">
      {/* Calendar Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Clara Kalender</h2>
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Alle Ihre Termine an einem Ort
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="dark:border-gray-600 dark:text-white"
            onClick={() => window.open('https://hub.clara360.de/apps/calendar', '_blank')}
          >
            <ExternalLink className="w-4 h-4 mr-2" />
            In Hub öffnen
          </Button>
          <Button size="sm" className="bg-green-600 hover:bg-green-700">
            <Plus className="w-4 h-4 mr-2" />
            Neuer Termin
          </Button>
        </div>
      </div>
      
      {/* View Selector */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button
            variant={currentView === 'dayGridMonth' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setCurrentView('dayGridMonth')}
            className={currentView === 'dayGridMonth' ? 'bg-blue-600 hover:bg-blue-700' : 'dark:border-gray-600 dark:text-white'}
          >
            Monat
          </Button>
          <Button
            variant={currentView === 'timeGridWeek' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setCurrentView('timeGridWeek')}
            className={currentView === 'timeGridWeek' ? 'bg-blue-600 hover:bg-blue-700' : 'dark:border-gray-600 dark:text-white'}
          >
            Woche
          </Button>
          <Button
            variant={currentView === 'timeGridDay' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setCurrentView('timeGridDay')}
            className={currentView === 'timeGridDay' ? 'bg-blue-600 hover:bg-blue-700' : 'dark:border-gray-600 dark:text-white'}
          >
            Tag
          </Button>
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchEvents()}
            disabled={loading}
            className="dark:border-gray-600 dark:text-white"
          >
            {loading ? <Loader className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </Button>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-gray-500" />
            <Input
              placeholder="Termine suchen..."
              className="pl-8 h-9 w-[200px] dark:bg-gray-800 dark:border-gray-700"
            />
          </div>
        </div>
      </div>
      
      {/* Calendar */}
      <Card className="dark:bg-gray-800 dark:border-gray-700">
        <CardContent className="p-4">
          <div className={`${loading ? 'opacity-60' : ''}`}>
            <FullCalendar
              plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
              initialView={currentView}
              headerToolbar={false}
              events={events}
              eventContent={renderEventContent}
              eventClick={handleEventClick}
              datesSet={handleDatesSet}
              height="auto"
              locale="de"
              firstDay={1} // Monday as first day
              buttonText={{
                today: 'Heute',
                month: 'Monat',
                week: 'Woche',
                day: 'Tag'
              }}
              dayHeaderFormat={{ weekday: 'short', day: 'numeric' }}
              allDayText="Ganztägig"
              noEventsText="Keine Termine"
              slotLabelFormat={{
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
              }}
              eventTimeFormat={{
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
              }}
              slotMinTime="07:00:00"
              slotMaxTime="21:00:00"
              businessHours={{
                daysOfWeek: [1, 2, 3, 4, 5], // Monday - Friday
                startTime: '09:00',
                endTime: '17:00'
              }}
              nowIndicator={true}
              weekNumbers={true}
              weekNumberFormat={{ week: 'numeric' }}
              weekText="KW"
              dayMaxEvents={3}
              moreLinkText="+ weitere"
              moreLinkClick="popover"
              editable={false}
              selectable={true}
              selectMirror={true}
              expandRows={true}
              stickyHeaderDates={true}
              themeSystem="standard"
            />
          </div>
        </CardContent>
      </Card>
      
      {/* Event Details Modal */}
      {showEventDetails && selectedEvent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg w-full max-w-md">
            <div className="p-4 border-b dark:border-gray-700 flex items-center justify-between">
              <h3 className="text-lg font-semibold dark:text-white">Termin Details</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowEventDetails(false)}
                className="h-8 w-8 p-0"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            
            <div className="p-4 space-y-4">
              <div>
                <h4 className="text-xl font-semibold dark:text-white">{selectedEvent.title}</h4>
                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 mt-1">
                  <Clock className="w-4 h-4" />
                  <span>
                    {formatDate(selectedEvent.start)} {formatTime(selectedEvent.start)} - {formatTime(selectedEvent.end)}
                  </span>
                </div>
              </div>
              
              {selectedEvent.extendedProps?.location && (
                <div className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-gray-600 dark:text-gray-300 mt-0.5" />
                  <span className="text-gray-600 dark:text-gray-300">
                    {selectedEvent.extendedProps.location}
                  </span>
                </div>
              )}
              
              {selectedEvent.extendedProps?.description && (
                <div className="border-t dark:border-gray-700 pt-3">
                  <p className="text-gray-600 dark:text-gray-300">
                    {selectedEvent.extendedProps.description}
                  </p>
                </div>
              )}
              
              {selectedEvent.extendedProps?.attendees && selectedEvent.extendedProps.attendees.length > 0 && (
                <div className="border-t dark:border-gray-700 pt-3">
                  <div className="flex items-center gap-2 mb-2">
                    <Users className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                    <span className="text-sm font-medium dark:text-white">Teilnehmer</span>
                  </div>
                  <div className="space-y-1">
                    {selectedEvent.extendedProps.attendees.map((attendee, index) => (
                      <div key={index} className="text-sm text-gray-600 dark:text-gray-300">
                        {attendee}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {selectedEvent.extendedProps?.category && (
                <div className="mt-2">
                  <Badge 
                    style={{ 
                      backgroundColor: CATEGORY_COLORS[selectedEvent.extendedProps.category],
                      color: 'white'
                    }}
                  >
                    {selectedEvent.extendedProps.category === 'meeting' ? 'Besprechung' : 
                     selectedEvent.extendedProps.category === 'maintenance' ? 'Wartung' :
                     selectedEvent.extendedProps.category === 'deadline' ? 'Frist' :
                     selectedEvent.extendedProps.category === 'reminder' ? 'Erinnerung' : 'Sonstiges'}
                  </Badge>
                </div>
              )}
            </div>
            
            <div className="p-4 border-t dark:border-gray-700 flex justify-between">
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" className="dark:border-gray-600 dark:text-white">
                  <Edit className="w-4 h-4 mr-1" />
                  Bearbeiten
                </Button>
                <Button variant="outline" size="sm" className="dark:border-gray-600 dark:text-white text-red-600 hover:text-red-700">
                  <Trash2 className="w-4 h-4 mr-1" />
                  Löschen
                </Button>
              </div>
              
              {selectedEvent.extendedProps?.hubUrl && (
                <Button 
                  size="sm" 
                  className="bg-blue-600 hover:bg-blue-700"
                  onClick={() => openInHub(selectedEvent.extendedProps.hubUrl)}
                >
                  <ExternalLink className="w-4 h-4 mr-1" />
                  In Hub öffnen
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Legend */}
      <div className="flex flex-wrap gap-4 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: CATEGORY_COLORS.meeting }}></div>
          <span className="text-gray-600 dark:text-gray-300">Besprechung</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: CATEGORY_COLORS.maintenance }}></div>
          <span className="text-gray-600 dark:text-gray-300">Wartung</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: CATEGORY_COLORS.deadline }}></div>
          <span className="text-gray-600 dark:text-gray-300">Frist</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: CATEGORY_COLORS.reminder }}></div>
          <span className="text-gray-600 dark:text-gray-300">Erinnerung</span>
        </div>
      </div>
      
      {/* Upcoming Events */}
      <Card className="dark:bg-gray-800 dark:border-gray-700">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 dark:text-white">
            <CalendarIcon className="w-5 h-5 text-blue-600" />
            Anstehende Termine
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {events.slice(0, 3).map((event) => (
              <div 
                key={event.id} 
                className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg cursor-pointer"
                onClick={() => {
                  setSelectedEvent({
                    title: event.title,
                    start: event.start,
                    end: event.end,
                    extendedProps: event.extendedProps
                  });
                  setShowEventDetails(true);
                }}
              >
                <div className="flex items-center gap-3">
                  <div 
                    className="w-12 h-12 rounded-lg flex items-center justify-center"
                    style={{ 
                      backgroundColor: `${CATEGORY_COLORS[event.extendedProps?.category || 'other']}20`,
                      color: CATEGORY_COLORS[event.extendedProps?.category || 'other']
                    }}
                  >
                    <CalendarIcon className="w-6 h-6" />
                  </div>
                  <div>
                    <h4 className="font-medium dark:text-white">{event.title}</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      {formatDate(event.start)} • {formatTime(event.start)}
                    </p>
                    {event.extendedProps?.location && (
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {event.extendedProps.location}
                      </p>
                    )}
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-8 w-8 p-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    openInHub(event.extendedProps?.hubUrl);
                  }}
                >
                  <ExternalLink className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ClaraCalendar;

